import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useUserStore } from 'store';
import MessagePreviewDialog from '../shared/MessagePreviewDialog';
import PublicFeedbackView from './public/PublicFeedbackView';

// --- Helpers (same as BookingSettingsTab) ---

const timeStringToDate = (timeStr) => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const dateToTimeString = (date) => {
  if (!date) return '';
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

// --- SettingsAccordion (same as BookingSettingsTab) ---

function SettingsAccordion({ id, expanded, onChange, title, children }) {
  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      disableGutters
      elevation={0}
      sx={{ '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', overflow: 'hidden', mb: 2 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: { xs: 2, sm: 3 } }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

// --- Main Component ---

export default function FeedbackSettingsTab({ onDirtyChange }) {
  const { t } = useTranslation();
  const { currentOrganization, fetchUser } = useUserStore();
  const orgId = currentOrganization?.organization_id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfigRaw] = useState(null);
  const [isConfigDirty, setIsConfigDirty] = useState(false);

  // Org-level toggle (separate from config)
  const [orgEnabled, setOrgEnabled] = useState(false);
  const [orgEnabledOriginal, setOrgEnabledOriginal] = useState(false);
  const isOrgDirty = orgEnabled !== orgEnabledOriginal;

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [urlError, setUrlError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [expanded, setExpanded] = useState({ timing: true, channel: false, google: false, followup: false });

  // Wrapped setter that marks dirty
  const setConfig = useCallback((updater) => {
    setConfigRaw((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return next;
    });
    setIsConfigDirty(true);
  }, []);

  // --- Data fetching ---

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get('/api/feedback-config/', {
        params: { organization: orgId },
      });
      setConfigRaw(response.data);
    } catch (err) {
      console.error('Failed to load feedback config:', err);
      setSnackbar({ open: true, message: t('feedback_settings.save_error'), severity: 'error' });
    }
  }, [orgId, t]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const enabled = currentOrganization?.customer_feedback_enabled || false;
      setOrgEnabled(enabled);
      setOrgEnabledOriginal(enabled);
      await fetchConfig();
      setLoading(false);
    };
    load();
  }, [fetchConfig, currentOrganization?.customer_feedback_enabled]);

  // --- Dirty tracking ---

  const hasChanges = isConfigDirty || isOrgDirty;

  const handleSave = async () => {
    setSaving(true);
    try {
      // 1. Save org toggle if changed
      if (isOrgDirty) {
        await axios.patch(`/api/organizations/${orgId}/`, {
          customer_feedback_enabled: orgEnabled,
        }, {
          params: { organization: orgId },
        });
      }

      // 2. Save config if changed
      if (isConfigDirty && config) {
        const response = await axios.patch('/api/feedback-config/', config, {
          params: { organization: orgId },
        });
        setConfigRaw(response.data);
      }

      // Refresh user store to get updated org flags
      await fetchUser();
      setOrgEnabledOriginal(orgEnabled);
      setIsConfigDirty(false);
      setSnackbar({ open: true, message: t('feedback_settings.saved'), severity: 'success' });
    } catch (err) {
      console.error('Failed to save feedback settings:', err);
      let errorMsg = t('feedback_settings.save_error');
      const data = err.response?.data;
      if (data?.detail) {
        errorMsg = data.detail;
      } else if (typeof data === 'object') {
        const messages = Object.values(data).flat().filter(Boolean);
        if (messages.length > 0) errorMsg = messages.join(', ');
      }
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  // --- Preview: what the customer gets with the settings on screen (saved or not) ---
  const loadPreview = useCallback(async (language) => {
    const response = await axios.post(
      '/api/feedback-config/preview/',
      { ...config, language },
      { params: { organization: orgId } },
    );
    return response.data;
  }, [config, orgId]);

  const renderPreviewPage = useCallback(
    (page, { language }) => <PublicFeedbackView previewData={page.data} previewLanguage={language} />,
    [],
  );

  useEffect(() => {
    onDirtyChange?.(hasChanges, () => handleSaveRef.current());
  }, [hasChanges, onDirtyChange]);

  // --- Accordion toggle ---
  const toggleAccordion = (panel) => (_, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [panel]: isExpanded }));
  };

  // --- URL validation ---
  const validateUrl = (url) => {
    if (!url) {
      setUrlError('');
      return;
    }
    try {
      new URL(url);
      setUrlError('');
    } catch {
      setUrlError(t('feedback_settings.google_review_url_invalid'));
    }
  };

  // --- Loading ---
  if (loading || !config) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
      <Box>
        {/* Main feature toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {t('feedback_settings.feature_toggle')}
            </Typography>
          </Box>
          <Switch
            checked={orgEnabled}
            onChange={(e) => setOrgEnabled(e.target.checked)}
            color="primary"
          />
        </Box>

        {!orgEnabled && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('feedback_settings.feature_description')}
          </Typography>
        )}

        {orgEnabled && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

            {/* Section 1: Timing */}
            <SettingsAccordion
              id="timing"
              expanded={!!expanded.timing}
              onChange={toggleAccordion('timing')}
              title={t('feedback_settings.timing_title')}
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('feedback_settings.trigger_state')}</InputLabel>
                    <Select
                      value={config.trigger_state || 'picked_up'}
                      label={t('feedback_settings.trigger_state')}
                      onChange={(e) => setConfig({ ...config, trigger_state: e.target.value })}
                    >
                      <MenuItem value="picked_up">{t('feedback_settings.trigger_picked_up')}</MenuItem>
                      <MenuItem value="completed">{t('feedback_settings.trigger_completed')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('feedback_settings.delay_days')}
                    type="number"
                    size="small"
                    fullWidth
                    value={config.delay_days ?? 3}
                    onChange={(e) => {
                      const val = Math.max(1, Math.min(30, parseInt(e.target.value, 10) || 1));
                      setConfig({ ...config, delay_days: val });
                    }}
                    inputProps={{ min: 1, max: 30 }}
                    helperText={t('feedback_settings.delay_days_help')}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TimePicker
                    label={t('feedback_settings.send_window_start')}
                    value={timeStringToDate(config.send_window_start || '09:00')}
                    onChange={(v) => setConfig({ ...config, send_window_start: dateToTimeString(v) })}
                    ampm={false}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TimePicker
                    label={t('feedback_settings.send_window_end')}
                    value={timeStringToDate(config.send_window_end || '17:00')}
                    onChange={(v) => setConfig({ ...config, send_window_end: dateToTimeString(v) })}
                    ampm={false}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.skip_weekends ?? true}
                        onChange={(e) => setConfig({ ...config, skip_weekends: e.target.checked })}
                      />
                    }
                    label={t('feedback_settings.skip_weekends')}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('feedback_settings.cooldown_days')}
                    type="number"
                    size="small"
                    fullWidth
                    value={config.per_customer_cooldown_days ?? 30}
                    onChange={(e) => {
                      const val = Math.max(0, parseInt(e.target.value, 10) || 0);
                      setConfig({ ...config, per_customer_cooldown_days: val });
                    }}
                    inputProps={{ min: 0 }}
                    helperText={t('feedback_settings.cooldown_days_help')}
                  />
                </Grid>
              </Grid>
            </SettingsAccordion>

            {/* Section 2: Channel */}
            <SettingsAccordion
              id="channel"
              expanded={!!expanded.channel}
              onChange={toggleAccordion('channel')}
              title={t('feedback_settings.channel_title')}
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                    {t('feedback_settings.channel')}
                  </Typography>
                  <RadioGroup
                    value={config.channel || 'email'}
                    onChange={(e) => setConfig({ ...config, channel: e.target.value })}
                  >
                    <FormControlLabel value="email" control={<Radio size="small" />} label={t('feedback_settings.channel_email')} />
                    <Box>
                      <FormControlLabel value="sms" control={<Radio size="small" />} label={t('feedback_settings.channel_sms')} />
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: -0.5, display: 'block' }}>
                        {t('feedback_settings.sms_billing_note')}
                      </Typography>
                    </Box>
                    <FormControlLabel value="both" control={<Radio size="small" />} label={t('feedback_settings.channel_both')} />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label={t('feedback_settings.custom_intro')}
                    multiline
                    rows={3}
                    size="small"
                    fullWidth
                    value={config.custom_intro_text || ''}
                    onChange={(e) => {
                      if (e.target.value.length <= 200) {
                        setConfig({ ...config, custom_intro_text: e.target.value });
                      }
                    }}
                    helperText={`${(config.custom_intro_text || '').length}/200 — ${t('feedback_settings.custom_intro_help')}`}
                    inputProps={{ maxLength: 200 }}
                  />
                </Grid>
              </Grid>
            </SettingsAccordion>

            {/* Section 3: Google Review */}
            <SettingsAccordion
              id="google"
              expanded={!!expanded.google}
              onChange={toggleAccordion('google')}
              title={t('feedback_settings.google_title')}
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <TextField
                    label={t('feedback_settings.google_review_url')}
                    size="small"
                    fullWidth
                    value={config.google_review_url || ''}
                    onChange={(e) => {
                      setConfig({ ...config, google_review_url: e.target.value });
                      if (urlError) setUrlError('');
                    }}
                    onBlur={(e) => validateUrl(e.target.value)}
                    error={!!urlError}
                    helperText={urlError || t('feedback_settings.google_review_url_help')}
                    placeholder="https://g.page/..."
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('feedback_settings.google_min_rating')}</InputLabel>
                    <Select
                      value={config.google_min_rating ?? 4}
                      label={t('feedback_settings.google_min_rating')}
                      onChange={(e) => setConfig({ ...config, google_min_rating: e.target.value })}
                    >
                      {[1, 2, 3, 4, 5].map((n) => (
                        <MenuItem key={n} value={n}>{n} {'★'.repeat(n)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {t('feedback_settings.google_min_rating_help')}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '13px' } }}>
                    {t('feedback_settings.google_policy_notice')}
                    {' '}
                    <Link
                      href="https://support.google.com/business/answer/3474122"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ fontSize: '13px' }}
                    >
                      {t('feedback_settings.google_guidelines_link')}
                    </Link>
                  </Alert>
                </Grid>
              </Grid>
            </SettingsAccordion>

            {/* Section 4: Low rating followup */}
            <SettingsAccordion
              id="followup"
              expanded={!!expanded.followup}
              onChange={toggleAccordion('followup')}
              title={t('feedback_settings.followup_title')}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={config.low_rating_followup_enabled ?? true}
                    onChange={(e) => setConfig({ ...config, low_rating_followup_enabled: e.target.checked })}
                  />
                }
                label={t('feedback_settings.followup_enabled')}
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 6, mt: 0.5 }}>
                {t('feedback_settings.followup_help')}
              </Typography>
            </SettingsAccordion>

            {/* Preview + Save buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<VisibilityIcon />}
                onClick={() => setPreviewOpen(true)}
                data-testid="feedback-preview-button"
              >
                {t('feedback_settings.preview_button')}
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !hasChanges}
                size="large"
              >
                {saving ? <CircularProgress size={20} /> : t('Save')}
              </Button>
            </Box>

            <MessagePreviewDialog
              open={previewOpen}
              onClose={() => setPreviewOpen(false)}
              title={t('feedback_settings.preview_title')}
              pageLabel={t('feedback_settings.preview_page_tab')}
              defaultLanguage={currentOrganization?.default_language}
              loadPreview={loadPreview}
              renderPage={renderPreviewPage}
            />
          </Box>
        )}

        {/* Show save button even when toggling off (need to save the org change) */}
        {!orgEnabled && isOrgDirty && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              size="large"
            >
              {saving ? <CircularProgress size={20} /> : t('Save')}
            </Button>
          </Box>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}
